import { type FormEventHandler, useContext, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import type { UseFormReturn } from "react-hook-form"
import { FormProvider } from "react-hook-form"
import { Box, Divider, IconButton, Stack, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"

import { getTypeParams } from "helpers/formatters/alertSettingsFormatters"
import {
  type AlertSettingParam,
  type AlertSettingType,
  type AlertSettingUpsertForm,
} from "types/alerts.types"
import Spinner from "widgets/common/Spinner"
import ControlledInput from "widgets/common/ControlledInput"
import FormButtons from "widgets/common/FormButtons"
import AlertSettingInput from "widgets/alerts/settings/AlertSettingInput"
import type { EntityType } from "types/common.types"
import { AlertSettingDrawerContext } from "./AlertSettingDrawerContext"
import AlertSettingEntityCard from "./AlertSettingEntityCard"

interface AlertSettingFormProps {
  entity: EntityType
  entityId: number
  isLoading: boolean
  title: string
  onSubmit: FormEventHandler<HTMLFormElement>
  methods: UseFormReturn<AlertSettingUpsertForm, any>
  types: AlertSettingType[]
  selectedTypeName: string
  onClose: () => void
  buttonText: string
  disableType?: boolean
}

function AlertSettingForm({
  entity,
  entityId,
  isLoading,
  title,
  onSubmit,
  methods,
  types,
  selectedTypeName,
  onClose,
  buttonText,
  disableType,
}: AlertSettingFormProps) {
  const { t } = useTranslation()
  const typesList = useMemo(
    () =>
      types?.map((type) => ({
        label: type.title,
        value: type.name,
      })),
    [types],
  )
  const { selectedType, typeParams } = useMemo(() => {
    const selectedType = types?.find((type) => type.name === selectedTypeName)
    const typeParams = getTypeParams(selectedTypeName, types, ["active", "send_sms"])
    return { selectedType, typeParams }
  }, [selectedTypeName, types])
  const hasSelectedType = selectedTypeName?.length > 0 && Boolean(selectedType)

  // Cancelling goes through the drawer so unsaved changes are confirmed first.
  const drawer = useContext(AlertSettingDrawerContext)
  const { isDirty } = methods.formState
  useEffect(() => {
    drawer?.setDirty(isDirty)
  }, [drawer, isDirty])
  const handleCancel = drawer ? drawer.requestClose : onClose

  const typeInput = (
    <>
      <ControlledInput
        type="select"
        placeholder=""
        name="type"
        label={t("alerts.ALERT_TYPE")}
        selectOptions={typesList}
        variant="outlined"
        disabled={disableType}
        defaultValue=""
      />
      {hasSelectedType && (
        <Typography component="p" sx={{ color: (theme) => theme.palette.text.secondary }}>
          {selectedType?.description}
        </Typography>
      )}
    </>
  )
  const paramInputs = hasSelectedType && (
    <Box sx={{ display: "flex", flexWrap: "wrap" }}>
      {typeParams?.map((param: AlertSettingParam) => (
        <AlertSettingInput
          param={param}
          key={param.id}
          entity={entity}
          entityId={entityId}
        />
      ))}
    </Box>
  )
  const buttons = (
    <FormButtons
      onClose={handleCancel}
      disabled={!hasSelectedType}
      buttonText={buttonText}
    />
  )

  return (
    <Stack sx={{ height: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ py: 2.5, px: 3.5 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t(title)}
        </Typography>
        <IconButton onClick={handleCancel} size="small" aria-label={t("generic.CLOSE")}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Divider />
      {isLoading ? (
        <Spinner />
      ) : (
        <Stack component="form" onSubmit={onSubmit} sx={{ flex: 1, minHeight: 0 }}>
          <FormProvider {...methods}>
            <Stack spacing={2.5} sx={{ py: 2.5, px: 3.5, overflowY: "auto", flex: 1 }}>
              <AlertSettingEntityCard entity={entity} entityId={entityId} />
              <Box>{typeInput}</Box>
              {paramInputs}
            </Stack>
            <Divider />
            <Box sx={{ py: 2.5, px: 3.5 }}>{buttons}</Box>
          </FormProvider>
        </Stack>
      )}
    </Stack>
  )
}

export default AlertSettingForm
